import {Injectable} from "@angular/core";
import {environment} from "@environments/environment";
import { HttpClient } from "@angular/common/http";
import {PaginationModel} from "@shared/models/pagination.model";
import {Observable} from "rxjs";
import {BillingContractSearchModel} from "@services/back-office/model/billingContractSearchModel";
import {PageResponseBillingContractModel} from "@services/back-office/model/pageResponseBillingContractModel";
import {BillingContractModel} from "@services/back-office/model/billingContractModel";
import {PageBillingCounterpartyModel} from "@services/back-office/model/pageBillingCounterpartyModel";
import {StringRequest} from "@services/back-office/model/stringRequest";
import {BillingCounterpartyModel} from "@services/back-office/model/billingCounterpartyModel";
import {BillingTransactionSearchModel} from "@services/back-office/model/billingTransactionSearchModel";
import {PageBillingTransactionModel} from "@services/back-office/model/pageBillingTransactionModel";
import {BillingActSearchModel} from "@services/back-office/model/billingActSearchModel";
import {PageBillingActModel} from "@services/back-office/model/pageBillingActModel";
import {BillingActModel} from "@services/back-office/model/billingActModel";
import {BillingTransactionModel} from "@services/back-office/model/billingTransactionModel";
import {BillingContractContactModel} from "@services/back-office/model/billingContractContactModel";
import {AssociationCalculationModel} from "@services/back-office/model/associationCalculationModel";
import {BillingContractDocumentModel} from "@services/back-office/model/billingContractDocumentModel";
import {BillingDahCounterpartyModel} from "@services/back-office/model/billingDahCounterpartyModel";
import {BillingDocumentRequest} from "@services/back-office/model/billingDocumentRequest";
import {BillingStatementRowModel} from "@services/back-office/model/billingStatementRowModel";
import {BillingStatementImportRequest} from "@services/back-office/model/billingStatementImportRequest";
import {BillingRefundDraftModel} from "@services/back-office/model/billingRefundDraftModel";
import {BillingRefundRequest} from "@services/back-office/model/billingRefundRequest";
import {PageBillingContractDocumentRowModel} from "@services/back-office/model/pageBillingContractDocumentRowModel";
import {BillingInvoiceSearchModel} from "@services/back-office/model/billingInvoiceSearchModel";
import {PageBillingInvoiceModel} from "@services/back-office/model/pageBillingInvoiceModel";
import {BillingInvoiceModel} from "@services/back-office/model/billingInvoiceModel";
import {BillingInvoiceHistoryModel} from "@services/back-office/model/billingInvoiceHistoryModel";
import {BillingDeletedActModel} from '@services/back-office/model/billingDeletedActModel';
import {RoleModel} from "@services/back-office/model/roleModel";
import {UserModel} from "@services/back-office/model/userModel";
// import {BillingCounterpartyUserModel} from "@services/back-office/model/billingCounterpartyUserModel";

@Injectable({
    providedIn: 'root'
})

export class BillingService {
    private baseUrl = environment.baseUrl;

    constructor(
        private http: HttpClient,
    ) {
    }

    /** Contracts */
    loadContracts(request: BillingContractSearchModel, pagination: PaginationModel): Observable<PageResponseBillingContractModel> {
        // a real Set serializes to {}, so both multi-value filters are handed over as arrays whichever way they arrive
        const queryParams = {
            ...request,
            statuses: request.statuses ? Array.from(request.statuses) : undefined,
            types: request.types ? Array.from(request.types) : undefined
        };
        const url = `${this.baseUrl}/back-office/v1/billing/contract/list?page=${pagination.number}&size=${pagination.size}`;
        return this.http.post<PageResponseBillingContractModel>(url, queryParams);
    }

    getDahCompanies(): Observable<BillingDahCounterpartyModel[]> {
        const url = `${this.baseUrl}/back-office/v1/billing/counterparty/dah`;
        return this.http.get<BillingDahCounterpartyModel[]>(url);
    }

    getNextContractNumber(): Observable<{ [key: string]: string }> {
        const url = `${this.baseUrl}/back-office/v1/billing/contract/numbers`;
        return this.http.get<{ [key: string]: string }>(url);
    }

    getCalculation(associationId: string): Observable<AssociationCalculationModel> {
        const url = `${this.baseUrl}/back-office/v1/billing/contract/${associationId}/calculation`;
        return this.http.get<AssociationCalculationModel>(url);
    }

    getContract(contractId: string): Observable<BillingContractModel> {
        const url = `${this.baseUrl}/back-office/v1/billing/contract/${contractId}`;
        return this.http.get<BillingContractModel>(url);
    }

    getContractContacts(contractId: string): Observable<BillingContractContactModel[]> {
        const url = `${this.baseUrl}/back-office/v1/billing/contract/${contractId}/contacts`;
        return this.http.get<BillingContractContactModel[]>(url);
    }

    setContractContacts(contractId: string, request: BillingContractContactModel[]): Observable<BillingContractContactModel[]> {
        const url = `${this.baseUrl}/back-office/v1/billing/contract/${contractId}/contacts`;
        return this.http.put<BillingContractContactModel[]>(url, request);
    }

    getAssociationContracts(associationId: string): Observable<BillingContractModel[]> {
        const url = `${this.baseUrl}/back-office/v1/billing/contract/list/organization/${associationId}`;
        return this.http.get<BillingContractModel[]>(url);
    }

    getCounterpartyContracts(counterpartyId: string): Observable<BillingContractModel[]> {
        const url = `${this.baseUrl}/back-office/v1/billing/contract/list/counterparty/${counterpartyId}`;
        return this.http.get<BillingContractModel[]>(url);
    }

    addContract(request: BillingContractModel): Observable<BillingContractModel> {
        const url = `${this.baseUrl}/back-office/v1/billing/contract`;
        return this.http.post<BillingContractModel>(url, request);
    }

    editContract(request: BillingContractModel): Observable<BillingContractModel> {
        const url = `${this.baseUrl}/back-office/v1/billing/contract`;
        return this.http.put<BillingContractModel>(url, request);
    }

    deleteContract(contractId: string): Observable<void> {
        const url = `${this.baseUrl}/back-office/v1/billing/contract/${contractId}`;
        return this.http.delete<void>(url);
    }

    /** Contract documents: invoices, acts, payments, refunds and debt adjustments in one list */
    listContractDocuments(contractId: string, pagination: PaginationModel): Observable<PageBillingContractDocumentRowModel> {
        const url = `${this.baseUrl}/back-office/v1/billing/contract/${contractId}/documents?page=${pagination.number}&size=${pagination.size}`;
        return this.http.post<PageBillingContractDocumentRowModel>(url, null);
    }

    /** Contracts files */
    getContractFiles(contractId: string): Observable<BillingContractDocumentModel[]> {
        const url = `${this.baseUrl}/back-office/v1/billing/contract/document/${contractId}/list`;
        return this.http.get<BillingContractDocumentModel[]>(url);
    }

    uploadContractTemplate(contractId: string, formData: FormData): Observable<void> {
        const url = `${this.baseUrl}/back-office/v1/billing/contract/document/${contractId}/template`;
        return this.http.post<void>(url, formData);
    }

    downloadContractTemplate(contractId: string): Observable<Blob> {
        const url = `${this.baseUrl}/back-office/v1/billing/contract/document/${contractId}/template`;
        return this.http.get(url, {responseType: 'blob'});
    }

    setDefaultContractTemplate(contractId: string): Observable<BillingContractDocumentModel[]> {
        const url = `${this.baseUrl}/back-office/v1/billing/contract/document/${contractId}/template`;
        return this.http.delete<BillingContractDocumentModel[]>(url);
    }

    uploadContractFile(contractId: string, formData: FormData, type: string, date: string): Observable<void> {
        const url = `${this.baseUrl}/back-office/v1/billing/contract/document/${contractId}/${type}/${date}/document`;
        return this.http.post<void>(url, formData);
    }

    generateContractFile(contractId: string, type: string, date: string): Observable<void> {
        const url = `${this.baseUrl}/back-office/v1/billing/contract/document/${contractId}/${type}/${date}`;
        return this.http.get<void>(url);
    }

    downloadContractFile(fileId: string, original: boolean): Observable<any> {
        const url = `${this.baseUrl}/back-office/v1/billing/contract/document/${fileId}?original=${original}`;
        return this.http.get(url, {responseType: 'arraybuffer', observe: 'response'});
    }

    signContractFile(fileId: string): Observable<void> {
        const url = `${this.baseUrl}/back-office/v1/billing/contract/document/${fileId}/sign`;
        return this.http.get<void>(url);
    }

    deleteContractFile(contractId: string): Observable<void> {
        const url = `${this.baseUrl}/back-office/v1/billing/contract/document/${contractId}`;
        return this.http.delete<void>(url);
    }

    toggleContractFileSignedState(documentId: string, sign: boolean): Observable<void> {
        const url = `${this.baseUrl}/back-office/v1/billing/contract/document/${documentId}/signed/${sign}`;
        return this.http.get<void>(url);
    }

    /** Counterparties */
    loadCounterparties(request: StringRequest, pagination: PaginationModel): Observable<PageBillingCounterpartyModel> {
        const params = {
            ...pagination?.number && {page: pagination.number},
            size: pagination.size ? pagination.size : 25,
        };

        const url = `${this.baseUrl}/back-office/v1/billing/counterparty/list`;
        return this.http.post<PageBillingCounterpartyModel>(url, request || {}, {params});
    }

    getCounterparty(counterpartyId: string): Observable<BillingCounterpartyModel> {
        const url = `${this.baseUrl}/back-office/v1/billing/counterparty/${counterpartyId}`;
        return this.http.get<BillingCounterpartyModel>(url);
    }

    addCounterparty(request: BillingCounterpartyModel): Observable<BillingCounterpartyModel> {
        const url = `${this.baseUrl}/back-office/v1/billing/counterparty`;
        return this.http.post<BillingCounterpartyModel>(url, request);
    }

    editCounterparty(request: BillingCounterpartyModel): Observable<BillingCounterpartyModel> {
        const url = `${this.baseUrl}/back-office/v1/billing/counterparty`;
        return this.http.put<BillingCounterpartyModel>(url, request);
    }

    deleteCounterparty(counterpartyId: string): Observable<void> {
        const url = `${this.baseUrl}/back-office/v1/billing/counterparty/${counterpartyId}`;
        return this.http.delete<void>(url);
    }

    toggleCounterpartyExperimentalFeatures(counterpartyId: string, enabled: boolean): Observable<void> {
        const url = `${this.baseUrl}/back-office/v1/billing/counterparty/${counterpartyId}/experimental-features/${enabled}`;
        return this.http.get<void>(url);
    }

    getCounterpartyRoles(): Observable<RoleModel[]> {
        const url = `${this.baseUrl}/back-office/v1/counterparty/role/list`;
        return this.http.get<RoleModel[]>(url);
    }

    addCounterpartyUser(counterpartyId: string, request: UserModel) {
        // const url = `${this.baseUrl}/back-office/v1/counterparty/user/${counterpartyId}/add`;
        // return this.http.post<BillingCounterpartyUserModel>(url, request);
    }

    deleteCounterpartyUser(counterpartyId: string) {
        const url = `${this.baseUrl}/back-office/v1/counterparty/user/${counterpartyId}`;
        return this.http.delete<void>(url);
    }

    /** Payments */
    loadPayments(request: BillingTransactionSearchModel, pagination: PaginationModel): Observable<PageBillingTransactionModel> {
        const url = `${this.baseUrl}/back-office/v1/billing/transaction?page=${pagination.number}&size=${pagination.size}`;
        return this.http.post<PageBillingTransactionModel>(url, request);
    }

    getPayment(paymentId: string): Observable<BillingTransactionModel> {
        const url = `${this.baseUrl}/back-office/v1/billing/transaction/${paymentId}`;
        return this.http.get<BillingTransactionModel>(url);
    }

    editPayment(request: BillingTransactionModel): Observable<BillingTransactionModel> {
        const url = `${this.baseUrl}/back-office/v1/billing/transaction`;
        return this.http.put<BillingTransactionModel>(url, request);
    }

    deletePayment(transactionId: string): Observable<void> {
        const url = `${this.baseUrl}/back-office/v1/billing/transaction/${transactionId}`;
        return this.http.delete<void>(url);
    }

    /** DF-6552: one day of every DAH account's bank statement, nothing stored yet. */
    getBankStatement(date: string): Observable<BillingStatementRowModel[]> {
        const url = `${this.baseUrl}/back-office/v1/billing/transaction/statement?date=${date}`;
        return this.http.get<BillingStatementRowModel[]>(url);
    }

    /** @returns how many of the chosen operations were new */
    importBankStatement(request: BillingStatementImportRequest): Observable<number> {
        const url = `${this.baseUrl}/back-office/v1/billing/transaction/statement`;
        return this.http.post<number>(url, request);
    }

    getRefundDraft(contractId: string): Observable<BillingRefundDraftModel> {
        const url = `${this.baseUrl}/back-office/v1/billing/transaction/refund/${contractId}/draft`;
        return this.http.get<BillingRefundDraftModel>(url);
    }

    createRefund(request: BillingRefundRequest): Observable<string> {
        const url = `${this.baseUrl}/back-office/v1/billing/transaction/refund`;
        return this.http.post<string>(url, request);
    }

    /** Acts */
    loadActs(request: BillingActSearchModel, pagination: PaginationModel): Observable<PageBillingActModel> {
        const url = `${this.baseUrl}/back-office/v1/billing/act?page=${pagination.number}&size=${pagination.size}`;
        return this.http.post<PageBillingActModel>(url, request);
    }

    loadDeletedActs(contractId: string): Observable<BillingDeletedActModel[]> {
        const url = `${this.baseUrl}/back-office/v1/billing/act/${contractId}/deleted`;
        return this.http.get<BillingDeletedActModel[]>(url);
    }

    downloadActFile(fileId: string, original: boolean): Observable<any> {
        const url = `${this.baseUrl}/back-office/v1/billing/act/document/${fileId}?original=${original}`;
        return this.http.get(url, {responseType: 'arraybuffer', observe: 'response'});
    }

    getAct(actId: string): Observable<BillingActModel> {
        const url = `${this.baseUrl}/back-office/v1/billing/act/${actId}`;
        return this.http.get<BillingActModel>(url);
    }

    addAct(contractId: string, request: BillingDocumentRequest): Observable<string> {
        const url = `${this.baseUrl}/back-office/v1/billing/act/${contractId}/add`;
        return this.http.post<string>(url, request);
    }

    editAct(request: BillingActModel): Observable<BillingActModel> {
        const url = `${this.baseUrl}/back-office/v1/billing/act`;
        return this.http.put<BillingActModel>(url, request);
    }

    deleteAct(actId: string, reason?: string): Observable<void> {
        const params = {
            ...reason && {reason}
        };
        const url = `${this.baseUrl}/back-office/v1/billing/act/${actId}`;
        return this.http.delete<void>(url, {params});
    }

    toggleActSignedState(actId: string, sign: boolean): Observable<void> {
        const url = `${this.baseUrl}/back-office/v1/billing/act/${actId}/signed/${sign}`;
        return this.http.get<void>(url);
    }

    signAct(actId: string): Observable<BillingActModel> {
        const url = `${this.baseUrl}/back-office/v1/billing/act/${actId}/sign`;
        return this.http.post<BillingActModel>(url, null);
    }

    /** Invoices */
    loadInvoices(request: BillingInvoiceSearchModel, pagination: PaginationModel): Observable<PageBillingInvoiceModel> {
        const url = `${this.baseUrl}/back-office/v1/billing/invoice?page=${pagination.number}&size=${pagination.size}`;
        return this.http.post<PageBillingInvoiceModel>(url, request);
    }

    getInvoice(invoiceId: string): Observable<BillingInvoiceModel> {
        const url = `${this.baseUrl}/back-office/v1/billing/invoice/${invoiceId}`;
        return this.http.get<BillingInvoiceModel>(url);
    }

    addInvoice(contractId: string, request: BillingDocumentRequest): Observable<boolean> {
        const url = `${this.baseUrl}/back-office/v1/billing/invoice/${contractId}/add`;
        return this.http.post<boolean>(url, request);
    }

    editInvoice(request: BillingInvoiceModel): Observable<BillingInvoiceModel> {
        const url = `${this.baseUrl}/back-office/v1/billing/invoice`;
        return this.http.put<BillingInvoiceModel>(url, request);
    }

    deleteInvoice(invoiceId: string): Observable<void> {
        const url = `${this.baseUrl}/back-office/v1/billing/invoice/${invoiceId}`;
        return this.http.delete<void>(url);
    }

    moveInvoice(invoiceId: string, send: boolean): Observable<BillingInvoiceModel> {
        const url = `${this.baseUrl}/back-office/v1/billing/invoice/${invoiceId}/move?send=${send}`;
        return this.http.post<BillingInvoiceModel>(url, null);
    }

    getInvoiceHistory(invoiceId: string): Observable<BillingInvoiceHistoryModel[]> {
        const url = `${this.baseUrl}/back-office/v1/billing/invoice/${invoiceId}/history`;
        return this.http.get<BillingInvoiceHistoryModel[]>(url);
    }

    downloadInvoiceFile(invoiceId: string): Observable<any> {
        const url = `${this.baseUrl}/back-office/v1/billing/invoice/document/${invoiceId}`;
        return this.http.get(url, {responseType: 'arraybuffer', observe: 'response'});
    }

    nonManagersToggle(contractId: string, date: string, ignore: boolean): Observable<void> {
        const url = `${this.baseUrl}/back-office/v1/billing/contract/${contractId}/${date}/${ignore}`;
        return this.http.get<void>(url);
    }
}
